import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineWarningAmber } from "react-icons/md";
import { TbInfoCircle } from "react-icons/tb";
import { SettingRow, NumberInput, Section, TextInput } from "./SettingsControls";
import { LegalMode, FinalSendMode, DunningLevelSendModes, DunningLetterOptions, LevelDispatch, DunningFines } from "./useSettings";

interface DunningSettingsProps {
    days:                   number[];
    legalMode:              LegalMode;
    paywiseApiKey:          string;
    onPaywiseApiKeyChange:  (v: string) => void;
    paywiseConfirmed:       boolean;
    onPaywiseConfirmedChange:(v: boolean) => void;
    dunningLevelSendModes:  DunningLevelSendModes;
    dunningFallbackToLetter: boolean;
    dunningLetterOptions:   DunningLetterOptions;
    dunningFinesEnabled:    boolean;
    dunningFines:           DunningFines;
    saved:                  boolean;
    dirty?:                 boolean;
    onChange:               (days: number[]) => void;
    onLegalModeChange:      (v: LegalMode) => void;
    onDunningLevelSendModes:(v: DunningLevelSendModes) => void;
    onDunningFallbackToLetter:(v: boolean) => void;
    onDunningLetterOptions: (v: DunningLetterOptions) => void;
    onDunningFinesEnabled:  (v: boolean) => void;
    onDunningFines:         (v: DunningFines) => void;
    onSave:                 () => void;
}

const SEND_OPTIONS: { value: FinalSendMode; labelKey: string }[] = [
    { value: "email",  labelKey: "settings.emailOnly" },
    { value: "letter", labelKey: "settings.letterOnly" },
    { value: "both",   labelKey: "settings.emailAndLetter" },
];

const DISPATCH_OPTIONS: { value: LevelDispatch; labelKey: string; costKey?: string }[] = [
    { value: "",        labelKey: "settings.dispatchStandard" },
    { value: "r1",      labelKey: "settings.dispatchR1",      costKey: "settings.dispatchR1Cost" },
    { value: "r2",      labelKey: "settings.dispatchR2",      costKey: "settings.dispatchR2Cost" },
];

// The server never sends the stored Paywise key, only this mask (backend utils/paywiseKey.ts);
// sending it back on save means "keep the stored key".
const PAYWISE_KEY_MASK = "••••••••";

function usesLetter(mode: FinalSendMode) {
    return mode === "letter" || mode === "both";
}

export function DunningSettings({
    days, legalMode, paywiseApiKey, onPaywiseApiKeyChange, paywiseConfirmed, onPaywiseConfirmedChange,
    dunningLevelSendModes, dunningFallbackToLetter, dunningLetterOptions,
    dunningFinesEnabled, dunningFines, saved, dirty,
    onChange, onLegalModeChange, onDunningLevelSendModes, onDunningFallbackToLetter,
    onDunningLetterOptions, onDunningFinesEnabled, onDunningFines, onSave,
}: DunningSettingsProps) {
    const { t } = useTranslation();
    // A key is on file: show an empty field with a "saved" placeholder. Typing replaces it;
    // clearing the field again keeps the stored key (state goes back to the mask).
    const [hasStoredKey, setHasStoredKey] = useState(paywiseApiKey === PAYWISE_KEY_MASK);
    useEffect(() => { if (paywiseApiKey === PAYWISE_KEY_MASK) setHasStoredKey(true); }, [paywiseApiKey]);
    const handlePaywiseKeyChange = (v: string) =>
        onPaywiseApiKeyChange(v === "" && hasStoredKey ? PAYWISE_KEY_MASK : v);

    const handleDayChange = (dayIdx: number, v: number) => {
        const next = [...days];
        next[dayIdx] = v;
        for (let i = dayIdx + 1; i < next.length; i++) {
            if (next[i] <= next[i - 1]) next[i] = next[i - 1] + 1;
        }
        onChange(next);
    };

    const handleDayBlur = (dayIdx: number, v: number) => {
        const next = [...days];
        const min = dayIdx > 0 ? next[dayIdx - 1] + 1 : 1;
        next[dayIdx] = Math.max(v, min);
        for (let i = dayIdx + 1; i < next.length; i++) {
            if (next[i] <= next[i - 1]) next[i] = next[i - 1] + 1;
        }
        onChange(next);
    };

    type LevelKey = keyof DunningLevelSendModes & keyof DunningLetterOptions & keyof DunningFines;

    const LEVELS: {
        label: string;
        badge: string;
        color: string;
        dayIdx: number;
        levelKey: LevelKey;
    }[] = [
        { label: t("dashboard.friendly"),  badge: "M1", color: "green",  dayIdx: 1, levelKey: "friendly"  },
        { label: t("dashboard.demanding"), badge: "M2", color: "orange", dayIdx: 2, levelKey: "demanding" },
        { label: t("dashboard.final"),     badge: "M3", color: "red",    dayIdx: 3, levelKey: "final"     },
    ];

    const setSendMode = (levelKey: LevelKey, v: FinalSendMode) =>
        onDunningLevelSendModes({ ...dunningLevelSendModes, [levelKey]: v });

    const setDispatch = (levelKey: LevelKey, v: LevelDispatch) =>
        onDunningLetterOptions({ ...dunningLetterOptions, [levelKey]: v });

    return (
        <Section title={t("settings.dunningIntervals")}>

            {/* ── Late fees ── */}
            <SettingRow title={t("settings.dunningFinesEnabled")} description={t("settings.dunningFinesEnabledDesc")}>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={dunningFinesEnabled}
                        onChange={(e) => onDunningFinesEnabled(e.target.checked)}
                    />
                    <span className="toggle-switch__track" />
                </label>
            </SettingRow>

            {/* ── Letter fallback ── */}
            <SettingRow title={t("settings.dunningFallbackToLetter")} description={t("settings.dunningFallbackToLetterDesc")}>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={dunningFallbackToLetter}
                        onChange={(e) => onDunningFallbackToLetter(e.target.checked)}
                    />
                    <span className="toggle-switch__track" />
                </label>
            </SettingRow>

            {dunningFallbackToLetter && (
                <div className="settings-info-banner settings-info-banner--neutral settings-info-banner--row">
                    <TbInfoCircle size={16} />
                    {t("settings.dunningFallbackEnabledNote")}
                </div>
            )}

            {!dunningFallbackToLetter && (() => {
                const m1Email = dunningLevelSendModes.friendly === "email";
                const m2Email = dunningLevelSendModes.demanding === "email";
                if (!m1Email && !m2Email) return null;
                const key = m1Email && m2Email
                    ? "settings.dunningFallbackDisabledWarnM1M2"
                    : m1Email
                        ? "settings.dunningFallbackDisabledWarnM1"
                        : "settings.dunningFallbackDisabledWarnM2";
                return (
                    <div className="settings-info-banner settings-info-banner--warn settings-info-banner--row">
                        <MdOutlineWarningAmber size={16} />
                        {t(key)}
                    </div>
                );
            })()}

            {/* ── M1 / M2 / M3 level cards ── */}
            {LEVELS.map(({ label, badge, color, dayIdx, levelKey }) => {
                const sendMode = dunningLevelSendModes[levelKey];
                const dispatch = dunningLetterOptions[levelKey];
                const hasLetter = usesLetter(sendMode);
                const isEmailOnly = sendMode === "email";

                return (
                    <div key={badge} className="dunning-level-card">
                        <div className="dunning-level-card__header">
                            <span className={`dunning__badge dunning__badge--${color}`}>{badge}</span>
                            <span className="dunning-level-card__name">{label}</span>
                        </div>

                        <div className="dunning-level-card__body">
                            {/* Days */}
                            <div className="dunning-level-card__row">
                                <span className="dunning-level-card__row-label">{t("settings.dayAfterSent")}</span>
                                <div className="input-group">
                                    <NumberInput
                                        value={days[dayIdx]}
                                        onChange={(v) => handleDayChange(dayIdx, v)}
                                        onBlur={(v)   => handleDayBlur(dayIdx, v)}
                                        min={days[dayIdx - 1] + 1}
                                    />
                                    <span className="settings-unit">{t("settings.days")}</span>
                                </div>
                            </div>

                            {/* Send mode */}
                            <div className="dunning-level-card__row">
                                <span className="dunning-level-card__row-label">{t("settings.sendVia")}</span>
                                <div className="dunning-level-send">
                                    {SEND_OPTIONS.map(({ value, labelKey }) => (
                                        <button
                                            key={value}
                                            className={`toggle-btn${sendMode === value ? " toggle-btn--active" : ""}`}
                                            onClick={() => setSendMode(levelKey, value)}
                                        >
                                            {t(labelKey)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Fine amount */}
                            {dunningFinesEnabled && (
                                <div className="dunning-level-card__row">
                                    <span className="dunning-level-card__row-label">{t("settings.dunningFineLabel")}</span>
                                    <div className="input-group">
                                        <NumberInput
                                            value={dunningFines[levelKey]}
                                            onChange={(v) => onDunningFines({ ...dunningFines, [levelKey]: v })}
                                            min={0}
                                        />
                                        <span className="settings-unit">€</span>
                                    </div>
                                </div>
                            )}

                            {/* M3 email-only warning */}
                            {badge === "M3" && isEmailOnly && (
                                <div className="dunning-level-card__warn">
                                    <MdOutlineWarningAmber size={15} />
                                    {t("settings.dunningM3EmailWarning")}
                                </div>
                            )}

                            {/* Per-level dispatch (only when letter is involved) */}
                            {hasLetter && (
                                <div className="dunning-level-card__row dunning-level-card__row--dispatch">
                                    <span className="dunning-level-card__row-label">{t("settings.letterDelivery")}</span>
                                    <div className="dunning-level-send">
                                        {DISPATCH_OPTIONS.map(({ value, labelKey, costKey }) => (
                                            <button
                                                key={value}
                                                className={`toggle-btn${dispatch === value ? " toggle-btn--active" : ""}`}
                                                onClick={() => setDispatch(levelKey, value)}
                                                title={costKey ? t(costKey) : undefined}
                                            >
                                                {t(labelKey)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* ── Inkasso / Legal ── */}
            <div className="dunning-level-card dunning-level-card--legal">
                <div className="dunning-level-card__header">
                    <span className="dunning-level-card__name">{t("dashboard.legal")}</span>
                </div>

                <div className="dunning-level-card__body">
                    {/* Days */}
                    <div className="dunning-level-card__row">
                        <span className="dunning-level-card__row-label">{t("settings.dayAfterSent")}</span>
                        <div className="input-group">
                            <NumberInput
                                value={days[4]}
                                onChange={(v) => handleDayChange(4, v)}
                                onBlur={(v)   => handleDayBlur(4, v)}
                                min={days[3] + 1}
                            />
                            <span className="settings-unit">{t("settings.days")}</span>
                        </div>
                    </div>

                    {/* Fine */}
                    {dunningFinesEnabled && (
                        <div className="dunning-level-card__row">
                            <span className="dunning-level-card__row-label">{t("settings.dunningFineLabel")}</span>
                            <div className="input-group">
                                <NumberInput
                                    value={dunningFines.legal}
                                    onChange={(v) => onDunningFines({ ...dunningFines, legal: v })}
                                    min={0}
                                />
                                <span className="settings-unit">€</span>
                            </div>
                        </div>
                    )}

                    {/* How to handle Inkasso */}
                    <div className="dunning-level-card__row dunning-level-card__row--legal-mode">
                        <span className="dunning-level-card__row-label">{t("settings.legalHandling")}</span>
                        <div className="dunning-legal-options">
                            <label
                                className={`dunning-legal-option${legalMode === "self" ? " dunning-legal-option--active" : ""}`}
                                onClick={() => onLegalModeChange("self")}
                            >
                                <input type="radio" className="dunning-legal-option__radio" checked={legalMode === "self"} onChange={() => onLegalModeChange("self")} />
                                <div className="dunning-legal-option__body">
                                    <span className="dunning-legal-option__label">{t("settings.legalSelf")}</span>
                                    <span className="dunning-legal-option__desc">{t("settings.legalSelfDesc")}</span>
                                </div>
                            </label>
                            <label
                                className={`dunning-legal-option${legalMode === "api" ? " dunning-legal-option--active" : ""}`}
                                onClick={() => onLegalModeChange("api")}
                            >
                                <input type="radio" className="dunning-legal-option__radio" checked={legalMode === "api"} onChange={() => onLegalModeChange("api")} />
                                <div className="dunning-legal-option__body">
                                    <span className="dunning-legal-option__label">{t("settings.legalApi")}</span>
                                    <span className="dunning-legal-option__desc">{t("settings.legalApiDesc")}</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Paywise API key + DSGVO confirm — only when legalMode = "api" */}
                    {legalMode === "api" && (
                        <>
                            <div className="dunning-level-card__row dunning-level-card__row--paywise-key">
                                <span className="dunning-level-card__row-label">{t("settings.paywiseApiKey")}</span>
                                <div className="dunning-paywise-key">
                                    <TextInput
                                        value={paywiseApiKey === PAYWISE_KEY_MASK ? "" : paywiseApiKey}
                                        onChange={handlePaywiseKeyChange}
                                        placeholder={hasStoredKey ? t("settings.paywiseApiKeyStoredPlaceholder") : t("settings.paywiseApiKeyPlaceholder")}
                                        label={t("settings.paywiseApiKey")}
                                    />
                                    <span className="dunning-paywise-key__hint">
                                        {paywiseApiKey
                                            ? <span className="dunning-paywise-key__saved">✓ {t("settings.paywiseApiKeyConnected")}</span>
                                            : <>
                                        <span className="dunning-paywise-key__desc">{t("settings.paywiseApiKeyDesc")}</span>
                                        <a href="https://www.paywise.de" target="_blank" rel="noreferrer" className="btn-outline btn-outline--secondary dunning-paywise-key__register">paywise.de ↗</a>
                                    </>
                                        }
                                    </span>
                                </div>
                            </div>
                            <div className="dunning-paywise-key__meta">
                                <input
                                    id="paywise-dsgvo"
                                    type="checkbox"
                                    checked={paywiseConfirmed}
                                    onChange={(e) => onPaywiseConfirmedChange(e.target.checked)}
                                />
                                <label htmlFor="paywise-dsgvo">{t("settings.paywiseDsgvoConfirm")}</label>
                            </div>
                        </>
                    )}
                </div>
            </div>

        </Section>
    );
}
